import { getMessaging, getToken, onMessage, MessagePayload } from 'firebase/messaging';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import toast from 'react-hot-toast';

let messaging: any = null;

export const initializeNotifications = async (userId: string): Promise<string | null> => {
  try {
    if (!db) {
      console.warn('Firebase not configured - notifications disabled');
      return null;
    }

    if (!process.env.REACT_APP_VAPID_KEY || process.env.REACT_APP_VAPID_KEY === 'your-vapid-key-here') {
      console.warn('VAPID key not configured - notifications disabled');
      return null;
    }

    if (!('serviceWorker' in navigator)) {
      console.warn('Service Worker not supported - notifications disabled');
      return null;
    }

    messaging = getMessaging();
    
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      return null;
    }

    const token = await getToken(messaging, {
      vapidKey: process.env.REACT_APP_VAPID_KEY
    });

    if (token) {
      await updateUserFCMToken(userId, token);
      return token;
    }
  } catch (error: any) {
    console.warn('Notifications not available:', error.message);
    
    if (error.message?.includes('service worker') || error.message?.includes('ServiceWorker')) {
      console.warn('Service worker registration failed - notifications disabled');
    } else if (error.code === 'messaging/failed-service-worker-registration') {
      console.warn('FCM service worker registration failed - notifications disabled');
    }
    
    return null;
  }
  
  return null;
};

const updateUserFCMToken = async (userId: string, token: string) => {
  if (!db) return;
  
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      fcmToken: token,
      lastTokenUpdate: new Date()
    });
  } catch (error) {
    console.error('Error updating FCM token:', error);
  }
};

export const setupForegroundMessageListener = () => {
  if (!messaging) {
    return;
  }
  
  try {
    onMessage(messaging, (payload: MessagePayload) => {
      const { notification, data } = payload;
      
      if (notification) {
        toast.success(`${notification.title}: ${notification.body}`, {
          duration: 6000,
          position: 'top-right',
          icon: getNotificationIcon(data?.type)
        });
        
        if (data) {
          setTimeout(() => {
            const shouldNavigate = window.confirm(`${notification.title}\n${notification.body}\n\nWould you like to view this?`);
            if (shouldNavigate) {
              handleNotificationClick(data);
            }
          }, 1000);
        }
      }
    });
  } catch (error) {
    console.warn('Error setting up foreground message listener:', error);
  }
};

const getNotificationIcon = (type?: string) => {
  switch (type) {
    case 'order_status':
      return '📦';
    case 'delivery_update':
      return '🚚';
    case 'promotion':
      return '🎉';
    default:
      return '🔔';
  }
};

const handleNotificationClick = (data: any) => {
  if (!data) return;
  
  switch (data.type) {
    case 'order_status':
      if (data.orderId) {
        window.location.href = `/order/${data.orderId}`;
      }
      break;
    case 'delivery_update':
      if (data.orderId) {
        window.location.href = `/order/${data.orderId}`;
      }
      break;
    case 'promotion':
      if (data.productId) {
        window.location.href = `/product/${data.productId}`;
      } else if (data.categoryId) {
        window.location.href = `/category/${data.categoryId}`;
      }
      break;
    default:
      window.location.href = '/';
  }
};

export const showLocalNotification = (title: string, body: string, data?: any) => {
  if ('serviceWorker' in navigator && 'Notification' in window) {
    navigator.serviceWorker.ready.then((registration) => {
      registration.showNotification(title, {
        body,
        icon: '/logo192.png',
        badge: '/logo192.png',
        data
      });
    });
  }
};

export interface OrderNotification {
  type: 'order_status';
  orderId: string;
  status: string;
  title: string;
  message: string;
}

export interface DeliveryNotification {
  type: 'delivery_update';
  orderId: string;
  agentName: string;
  estimatedTime: string;
  title: string;
  message: string;
}

export interface PromotionNotification {
  type: 'promotion';
  productId?: string;
  categoryId?: string;
  discount?: number;
  title: string;
  message: string;
}

export type NotificationType = OrderNotification | DeliveryNotification | PromotionNotification;
